import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface ExportToExcelOptions<T> {
  fileName: string;           // e.g. "Appointment_Details_09-01-2025"
  sheetName?: string;         // default = "Sheet1"
  title?: string;             // optional title row
  titleDate?: string;         // optional date in title
  data: T[];                  // array of objects [{ col1: val, col2: val }]
}

export async function exportToExcel<T extends Record<string, any>>({
  fileName,
  sheetName = "Sheet1",
  title,
  titleDate,
  data,
}: ExportToExcelOptions<T>): Promise<void> {
  if (!data || data?.length === 0) {
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  let currentRow = 1;

  // ===== Title Row =====
  if (title) {
    const colsCount = Object.keys(data[0]).length;
    worksheet.mergeCells(currentRow, 1, currentRow, colsCount);
    const titleCell = worksheet.getCell(currentRow, 1);
    titleCell.value = titleDate ? `${title} - ${titleDate}` : title;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: "left" };
    currentRow++;
  }

  // ===== Headers =====
  const headers = Object.keys(data[0]);
  const headerRow = worksheet.addRow(headers);

  headerRow.font = { bold: true };
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    if (colNumber === 1) {
      cell.alignment = { horizontal: "center" };
    }
  });

  // ===== Data Rows =====
  data.forEach((row) => {
    const dataRow = worksheet.addRow(Object.values(row));
    dataRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber === 1) {
        cell.alignment = { horizontal: "center" };
      }
    });
  });

  // ===== Auto-fit Columns =====
  worksheet.getColumn(1).width = 6; // Sr. No column fixed width
  worksheet.columns.forEach((column, index) => {
    if (index !== 0 && column && column.eachCell) {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    }
  });

  // ===== Download File =====
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`
  );
}
